import { useState } from 'react';

import { ExportDialog } from '@/components/ExportDialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import type { StaffListParams, StaffStatus } from '@/types/staff';
import { staffApi, type StaffExportFilters } from '../api/staff.api';
import {
  NON_TEACHING_FILTER,
  STAFF_ROLE_FILTER_OPTIONS,
  STAFF_STATUS_FILTER_OPTIONS,
} from '../constants';

export interface StaffExportDialogProps {
  /** The directory's filters right now — the dialog starts from them. */
  pageFilters: StaffListParams;
  triggerClassName?: string;
}

/** The staff directory's Export: who, which details, Excel or PDF. */
export function StaffExportDialog({ pageFilters, triggerClassName }: StaffExportDialogProps) {
  const [filters, setFilters] = useState<StaffExportFilters>(() => fromPage(pageFilters));

  const patch = (values: Partial<StaffExportFilters>) =>
    setFilters((current) => ({ ...current, ...values }));

  // Narrowings the directory's figures open that have no dropdown here. Named,
  // so nobody downloads "all staff" and gets a quietly filtered file.
  const carried = [
    filters.teaching && 'teaching staff',
    filters.invitePending && 'invites not yet accepted',
    filters.subjectUuid && 'one subject',
  ].filter(Boolean);

  const today = new Date().toISOString().slice(0, 10);

  return (
    <ExportDialog
      title="Download staff list"
      documentTitle="Staff directory"
      fileName={`staff-directory-${today}`}
      noun={{ one: 'staff member', other: 'staff members' }}
      storageKey="export-fields:staff"
      triggerClassName={triggerClassName}
      loadFields={staffApi.exportFields}
      fetchRows={(fields) => staffApi.exportRows(filters, fields)}
      onOpen={() => setFilters(fromPage(pageFilters))}
      filters={
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Input
              label="Name, email or employee ID"
              type="search"
              autoComplete="off"
              placeholder="Everyone"
              value={filters.search ?? ''}
              onChange={(event) => patch({ search: event.target.value })}
            />
            <Select
              label="Role"
              options={[{ value: '', label: 'All roles' }, ...STAFF_ROLE_FILTER_OPTIONS]}
              value={filters.nonTeaching ? NON_TEACHING_FILTER : (filters.staffType ?? '')}
              // Non-teaching is a group rather than a type, so it travels as its
              // own flag — and a subject means something only for a teacher.
              onChange={(event) => {
                const isNonTeaching = event.target.value === NON_TEACHING_FILTER;
                patch({
                  staffType: isNonTeaching ? undefined : event.target.value || undefined,
                  nonTeaching: isNonTeaching ? true : undefined,
                  teaching: undefined,
                  subjectUuid: event.target.value === 'TEACHER' ? filters.subjectUuid : undefined,
                });
              }}
            />
            <Select
              label="Status"
              options={[{ value: '', label: 'All statuses' }, ...STAFF_STATUS_FILTER_OPTIONS]}
              value={filters.status ?? ''}
              onChange={(event) =>
                patch({ status: (event.target.value as StaffStatus) || undefined })
              }
            />
          </div>

          {carried.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 text-sm text-ink-2">
              <span>Also limited to {carried.join(', ')}, as on the directory.</span>
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  patch({ teaching: undefined, invitePending: undefined, subjectUuid: undefined })
                }
              >
                Remove
              </Button>
            </div>
          )}
        </div>
      }
    />
  );
}

/** The page's filters without paging. */
function fromPage({ page: _page, limit: _limit, ...filters }: StaffListParams): StaffExportFilters {
  return filters;
}
